/**
 * KommissionierscheinPDF
 *
 * Toplama irsaliyesi: depo personelinin rafları dolaşıp ürünleri
 * toplaması için. Fiyat yok; öncelik lagerort ve miktar. Lagerort'a
 * göre sıralı liste + picker imza alanı.
 */
import {
  fetchPdfSettings,
  downloadPdfFromHtml,
  wrapPdfDocument,
  renderCompanyHeader,
  renderFooter,
  formatDate,
  sortItemsByLagerort,
  escapeHtml,
} from '@/lib/pdfRenderer';
import { getOrderItems } from '@/lib/orderFields';
import { getVorlage, textZuHtml } from '@/lib/dokumentVorlagen';

/** Kalem için ürün görselini bulur: varyant görseli > ürün görseli */
function resolveItemImage(item, products) {
  const product = products.find((p) => p.id === item.product_id);
  const variantValue = item.variant_label || item.variant_value || item.variant;
  if (product && variantValue) {
    let variants = product.variants || [];
    if (typeof variants === 'string') {
      try { variants = JSON.parse(variants); } catch { variants = []; }
    }
    const variant = (Array.isArray(variants) ? variants : [])
      .find((v) => v.value === variantValue);
    if (variant?.image) return variant.image;
  }
  return item.image || product?.image_url || '';
}

function renderTemplate(order, settings, products, sortedItems) {
  const today = formatDate(new Date());
  const pickedAt = order.picked_at ? formatDate(order.picked_at) : today;
  const vorlage = getVorlage(settings, 'kommissionierschein');
  const ctx = {
    'kunde.name': order.lead_name || '', 'kunde.nummer': (order.lead_id || '').substring(0, 4).toUpperCase(),
    'firma.name': settings.company_name, 'firma.geschaeftsfuehrer': settings.geschaeftsfuehrer, 'firma.ansprechpartner': settings.ansprechpartner,
    'beleg.nummer': order.picking_number || '', 'beleg.datum': pickedAt, 'bestellung.nummer': order.order_number || '',
  };

  const rows = sortedItems.map((item) => {
    const imageUrl = resolveItemImage(item, products);
    const imageCell = imageUrl
      ? `<img src="${escapeHtml(imageUrl)}" alt=""
             style="width:44px;height:44px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;background:#fff"
             onerror="this.style.display='none'" />`
      : `<div style="width:44px;height:44px;border:1px dashed #d1d5db;border-radius:6px;margin:0 auto"></div>`;

    return `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:center;width:60px">
          ${imageCell}
        </td>
        <td style="padding:8px;border-bottom:1px solid #eee">
          <div style="font-weight:700;font-size:12px">${escapeHtml(item.product_name || item.name || '')}</div>
          ${item.variant_label || item.variant_value || item.variant
            ? `<div style="font-size:10px;color:#555">Variante: ${escapeHtml(item.variant_label || item.variant_value || item.variant)}</div>` : ''}
          ${item.product_sku
            ? `<div style="font-size:10px;color:#999;font-family:monospace">SKU: ${escapeHtml(item.product_sku)}</div>` : ''}
        </td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:center">
          <span style="display:inline-block;padding:4px 10px;background:#f0f9ff;border:1px solid #bae6fd;border-radius:6px;font-family:monospace;color:#0369a1;font-weight:700;font-size:13px;white-space:nowrap">
            ${escapeHtml(item._lagerort || '—')}
          </span>
        </td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:center">
          <div style="font-weight:700;font-size:16px">${item.quantity}</div>
          <div style="font-size:9px;color:#999;text-transform:uppercase">${escapeHtml(item.unit || 'Stück')}</div>
        </td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:center">
          <div style="width:28px;height:28px;border:2px solid #bbb;border-radius:4px;margin:0 auto"></div>
        </td>
      </tr>
    `;
  }).join('');

  // Yalnızca gövde HTML'i döner — tekil indirme wrapPdfDocument ile
  // sarar; Warenausgang paketi (KM+LS+LS tek PDF) gövdeyi doğrudan alır.
  return `
      ${renderCompanyHeader(settings)}

      <div style="display:flex;justify-content:space-between;margin-bottom:24px">
        <div>
          <h1 style="font-size:22px;margin:0 0 6px 0;color:#6d28d9">KOMMISSIONIERSCHEIN</h1>
          <div style="color:#666;font-size:11px">${textZuHtml(vorlage.untertitel, ctx)}</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#333">
          <div><strong>Nummer:</strong> ${escapeHtml(order.picking_number || '(wird vergeben)')}</div>
          <div><strong>Datum:</strong> ${pickedAt}</div>
          <div><strong>Bestellung:</strong> ${escapeHtml(order.order_number || '')}</div>
          ${order.picked_by ? `<div><strong>Picker:</strong> ${escapeHtml(order.picked_by)}</div>` : ''}
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px;font-size:11px">
        <div style="padding:12px;background:#faf5ff;border-left:3px solid #a855f7">
          <div style="font-size:9px;color:#7e22ce;font-weight:700;text-transform:uppercase;margin-bottom:4px">Kunde</div>
          <div style="font-weight:600">${escapeHtml(order.lead_name || '')}</div>
          ${order.lead_address ? `<div style="color:#666">${escapeHtml(order.lead_address)}</div>` : ''}
        </div>
        <div style="padding:12px;background:#f8fafc;border-left:3px solid #64748b">
          <div style="font-size:9px;color:#334155;font-weight:700;text-transform:uppercase;margin-bottom:4px">Hinweise</div>
          <div style="color:#555">${escapeHtml(order.notes || order.notizen || '—')}</div>
        </div>
      </div>

      <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:24px">
        <thead>
          <tr style="background:#172554;color:#fff">
            <th style="padding:8px;text-align:center;font-size:10px;text-transform:uppercase;width:60px">Bild</th>
            <th style="padding:8px;text-align:left;font-size:10px;text-transform:uppercase">Artikel</th>
            <th style="padding:8px;text-align:center;font-size:10px;text-transform:uppercase;width:100px">Lagerort</th>
            <th style="padding:8px;text-align:center;font-size:10px;text-transform:uppercase;width:70px">Menge</th>
            <th style="padding:8px;text-align:center;font-size:10px;text-transform:uppercase;width:50px">OK</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      <div style="margin-top:40px;display:grid;grid-template-columns:1fr 1fr;gap:32px">
        <div>
          <div style="border-bottom:1px solid #111;height:48px"></div>
          <div style="font-size:10px;color:#555;margin-top:4px">${textZuHtml(vorlage.unterschrift_links, ctx)}</div>
        </div>
        <div>
          <div style="border-bottom:1px solid #111;height:48px"></div>
          <div style="font-size:10px;color:#555;margin-top:4px">${textZuHtml(vorlage.unterschrift_rechts, ctx)}</div>
        </div>
      </div>

      ${renderFooter(settings)}
  `;
}

/**
 * Gövde HTML üretici (kombine PDF için dışa açık).
 * getOrderItems: positionen/items alanını dizi VEYA JSON string olarak
 * güvenli okur; kalemler Lagerort'a göre sıralanır.
 */
export function buildKommissionierscheinBody(order, settings, products = []) {
  const rawItems = getOrderItems(order);
  const sortedItems = sortItemsByLagerort(rawItems, products);
  return renderTemplate(order, settings, products, sortedItems);
}

export async function downloadKommissionierschein(order, products = [], customSettings) {
  const settings = customSettings || await fetchPdfSettings();
  const body = buildKommissionierscheinBody(order, settings, products);
  const html = wrapPdfDocument(body, `Kommissionierschein ${order.picking_number || ''}`);
  const fileName = order.picking_number || `KM-${Date.now()}`;
  await downloadPdfFromHtml(html, fileName);
}

export default { downloadKommissionierschein, buildKommissionierscheinBody };
